import React, { useEffect, useMemo, useRef, useState } from "react";
import { vhMinus } from 'utils/textZoom'
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";

import { getErrorMessage } from "utils";
import { serverFieldErrors } from "utils/serverFieldErrors";

import { CATEGORY_TYPES, CATEGORY_DISPLAY_NAMES } from "constants/categories";
import { getCategoryIcon } from "constants/categoryIcon";
import { useSnackbar } from "contexts";
import { useProduct, useProductMutations, useFieldMappingConfig, useProductSyncMode } from "hooks/api";
import { useCurrentInstallation } from "hooks/api/useInstallations";
import { useWorkOSAuth } from "hooks";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { ROUTES } from "routes";
import { PRODUCT_RULES } from "validation/product";
// VRG-777 — product media can be images AND video in one ordered list. The accept list
// is SHARED with the API's own kind table (held in agreement by a parity guard), and
// `isVideoUrl` is the same predicate every thumbnail consumer reads.
import {
  PRODUCT_MEDIA_ACCEPT,
  PRODUCT_MEDIA_ACCEPT_EXTENSIONS,
  PRODUCT_MEDIA_ACCEPT_MESSAGE,
  PRODUCT_MEDIA_ACCEPT_MIME_TYPES,
  isVideoUrl,
} from "@repo/types";
import type { ImportProductFromUrlResponse } from "services";

// Tooltip from MUI, not @repo/ui — that package's Tooltip is a 0-byte stub that
// resolves in TypeScript and exports nothing at runtime (working-conventions §47).
import { Box, Stack, Typography, IconButton, Collapse, Tooltip, Link as MuiLink } from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { TagIcon, CircleAlertIcon, ChevronDownIcon } from "lucide-react";

import { Button, Switch, Card, Alert, SectionNav, FieldLabel } from "@repo/ui";
import { ConfirmSettingModal } from "components/feedback/modals/ConfirmSettingModal";
// `TextField` below is the REACT-HOOK-FORM field from this app's own components
// directory, NOT `@repo/ui`'s component of the same name — that one is a styled MUI
// TextField taking a plain MUI prop surface. The two are interchangeable to the eye and
// their prop sets overlap (`name`, `value`, `type`, `inputProps`, `sx`, `onValueChange`
// are valid on both), so a call site written for the wrong one type-checks most of the
// way and fails on the rest. That is VRG-791's second error. The one here needs the whole
// `useForm()` return spread in as `{...methods}`; if a call site does not have `methods`
// to spread, it wants `@repo/ui`'s and should import it under a distinct name.
import {
  Header,
  Search,
  SelectField,
  TextField,
  DatePickerField,
  RichTextEditorField,
  FileUploadField,
  ProductModelField,
  RevealVideoField,
  ScanMediaField,
  ProductPreview,
  ProductSyncModeMenu,
  SetDefaultTagModal,
  InfoTooltip,
  StickyFormHeader,
  TocSpacer,
} from "components";
import { ProductCustomFieldsCard } from "./components/ProductCustomFieldsCard";
import { CertificateFieldsCard } from "./components/CertificateFieldsCard";
import { ContactCardFields } from "./components/ContactCardFields";
import { ProductEditPageSkeleton } from "./ProductEditPageSkeleton";
// Section wrapper styles, shared with this page's skeleton (and the owner form) so they cannot
// drift — see that module's docstring for why it isn't exported from here.
import { FORM_SECTION_SX } from "utils/formSectionSx";
import { zoomOf } from "utils/textZoom";

/**
 * VRG-101 / VRG-104 — curated product custom fields. **ON since VRG-104**, which is
 * the condition this gate was waiting on: the certificate now renders them (`text`
 * rows into the product-details table, `rich`/`link` into a titled brand block below
 * the description), so curating a field no longer writes into a void.
 *
 * It was deliberately `false` from VRG-101 until then — the card, the hook and the
 * `@Put(':productId/custom-fields')` endpoint all worked, but curating fields that
 * show nowhere is a confusing dead toggle, so curation and render shipped together.
 *
 * Kept as a named constant rather than deleted: it is the one switch that hides the
 * whole surface if the render side has to be rolled back, and the name is what makes
 * either state read as a decision (VRG-415) rather than a mistake.
 */
const CUSTOM_FIELDS_ENABLED = true;

/**
 * ⚠️ EXPERIENCE IS OFF — VRG-787-Certificate.Experience.Section.
 *
 * Spencer's call, 2026-10-31: the authoring card here AND the section it feeds on the
 * certificate are both hidden until the design is settled. The certificate side has the
 * full reasoning (`apps/app/src/pages/CertificatePage/certificateSections.ts`); the short
 * version is that it was filed under a HISTORY label beside the record's audit trail,
 * which is a claim about what kind of thing a brand's narrative is, and that claim was
 * never discussed.
 *
 * Hiding BOTH halves together is the point. A card that saves copy to a column nothing
 * renders is worse than no card: a brand writes the story, saves it, and finds nothing on
 * the certificate, with no way to tell a broken feature from one that is switched off.
 *
 * Nothing is deleted. `experience_content` keeps its column and its data, the field stays
 * registered on the form (so a value already saved round-trips untouched rather than being
 * blanked by a Save that no longer submits it), and flipping this to `true` brings the
 * card back exactly as it was — pair it with the flag on the certificate side.
 */
const EXPERIENCE_ENABLED = false;

/**
 * Every field name the form registers. Exists so `useForm` keeps the full
 * field-name union once `defaultValues` is supplied (VRG-353) — see the
 * useForm call. Optional throughout: create mode starts empty and a URL import
 * fills in only what the listing page happened to carry.
 */
type ProductFormValues = {
  title?: string;
  type?: string;
  description?: string;
  manufacture_date?: Date | string | null;
  sku?: string;
  serialNumber?: string;
  // `| null` because that is what the API returns for an unset value, and the
  // data-load effect setValue()s it straight through. The Controller below
  // renders `field.value == null ? "" : …`, so null reads as an empty field.
  product_url?: string | null;
  images?: any;
  available?: boolean;
  quantity?: number | string;
  // VRG-323 — where the tag sits on THIS item. Both fall back to the company-wide
  // defaults (Settings → Installation) when left blank.
  scan_instructions?: string;
  scan_media_url?: string | null;
  // VRG-782 — the Experience screen's body on this SKU's certificates. A real form
  // field saved by the main product Save, unlike the Custom fields / Certificate
  // fields cards below, which each have their own endpoint.
  experience_content?: string | null;
  // VRG-720 — staff-only "claim on scan" switch. Registered only for a platform_admin; every
  // other user's form never carries it, so their save never names it.
  claim_on_scan?: boolean;
  // VRG-720 — the contact card. Every key is a string in the form (blank = not set); the payload
  // builder below drops blanks and sends `null` when nothing is left, which clears a stored card.
  contact_card?: {
    name?: string;
    title?: string;
    company?: string;
    phone?: string;
    email?: string;
    website?: string;
    photo_url?: string | null;
    socials?: { network: string; url: string }[];
  };
  // VRG-749 — the product's GLB; `null` clears it.
  model_url?: string | null;
  // VRG-790 — the reveal: media pair plus the independent switch. Three values, and the
  // switch is separate on purpose — a brand uploads a drop's reveal days before it goes
  // live, so the upload must not BE the launch (see the migration's argument).
  reveal_video_url?: string | null;
  reveal_poster_url?: string | null;
  reveal_enabled?: boolean;
};

/** The form's contact-card values -> the API shape: blanks dropped, `null` when nothing is left. */
const buildContactCard = (card: ProductFormValues["contact_card"]) => {
  const text = (v?: string | null) => (typeof v === "string" ? v.trim() : "");
  const out: Record<string, unknown> = {};
  for (const key of ["name", "title", "company", "phone", "email", "website"] as const) {
    const value = text(card?.[key]);
    if (value) out[key] = value;
  }
  if (card?.photo_url) out.photo_url = card.photo_url;
  const socials = (card?.socials ?? [])
    .map((s) => ({ network: s.network, url: text(s.url) }))
    .filter((s) => s.url);
  if (socials.length) out.socials = socials;
  return Object.keys(out).length ? out : null;
};

// Section config drives BOTH the left TOC and the search filter from one source.
// STANDING RULE: when a new field/card is added to the Edit form, add its entry
// here (id + label + the form field names it owns + search keywords) so it shows
// up in the TOC and is reachable by the filter. See working-conventions.md.
type EditSection = {
  id: string;
  label: string;
  fields: string[];
  keywords: string[];
};

const SECTIONS: EditSection[] = [
  { id: "media", label: "Media", fields: ["images"], keywords: ["media", "image", "images", "photo", "picture"] },
  { id: "basic", label: "Basic info", fields: ["title", "description", "type"], keywords: ["basic", "title", "name", "description", "type", "category"] },
  { id: "specs", label: "Specifications", fields: ["manufacture_date", "sku", "serialNumber", "product_url"], keywords: ["spec", "specification", "sku", "serial", "manufacture", "date", "url", "link"] },
  { id: "scan", label: "Scan instructions", fields: ["scan_instructions", "scan_media_url"], keywords: ["scan", "tag", "nfc", "instructions", "where", "location", "video"] },
  // VRG-782 — the brand's narrative about the product, shown on its own certificate
  // screen. In SECTIONS (not a separate edit-only section like the two curation cards)
  // because it is an ordinary column on the product, saved by the product Save — so it
  // belongs on create as well as edit, which one component gives for free (§30).
  { id: "experience", label: "Experience", fields: ["experience_content"], keywords: ["experience", "story", "narrative", "about", "craft", "care", "material", "certificate"] },
  // VRG-749 — its own section rather than a field under "Media". Media holds the
  // `images` gallery, and a 3D model is not another image in that set: it REPLACES the
  // gallery's first frame as the hero wherever it is present, so filing it there would
  // read as "one more picture" and hide the fact that uploading one changes what the
  // certificate leads with. The TOC + search filter both key off this array (§30), so a
  // brand can find it by typing "3d", "model", "glb", "rotate" or "ar".
  { id: "model", label: "3D model", fields: ["model_url"], keywords: ["3d", "model", "glb", "gltf", "rotate", "spin", "ar", "augmented"] },
  // VRG-790 — its own section rather than a field under "Media", for the 3D model's
  // reason and more strongly. Media holds the `images` gallery; a reveal is not another
  // image in that set — it is a MOMENT that plays before the certificate is reachable at
  // all, so filing it with the photos would hide that uploading one changes what happens
  // when a customer taps the product. The TOC + search filter both key off this array
  // (§30), so a brand can find it by typing "reveal", "video", "drop" or "animation".
  { id: "reveal", label: "Reveal", fields: ["reveal_video_url", "reveal_poster_url", "reveal_enabled"], keywords: ["reveal", "video", "animation", "drop", "launch", "intro", "poster", "unboxing", "scan"] },
  { id: "inventory", label: "Inventory", fields: ["available", "quantity"], keywords: ["inventory", "availability", "stock", "quantity", "count"] },
  // VRG-720 — the contact card released to certificate viewers who have access. Any user who can
  // edit the product can edit it (no extra permission, §24).
  { id: "contact", label: "Contact card", fields: ["contact_card"], keywords: ["contact", "card", "business", "vcard", "phone", "email", "website", "social", "linkedin", "instagram", "name", "title", "company", "photo"] },
];

/**
 * A section by id, for the `<Collapse in={…}>` gates below.
 *
 * Those gates used to index `SECTIONS` POSITIONALLY (`SECTIONS[0]`…`SECTIONS[3]`), which is
 * only correct until someone inserts a section in the middle — at which point every card
 * below the insertion point is gated by its NEIGHBOUR's visibility, and the search filter
 * hides the wrong cards with nothing failing to compile. Inserting "Scan instructions"
 * between Specifications and Inventory is exactly that edit, so the lookup is by id now and
 * the ordering of this array is free to change again.
 */
/**
 * VRG-782 — what the Experience editor's value should be PERSISTED as.
 *
 * `RichTextEditorField` does not round-trip an emptied field to `""`. Clear every
 * character and it hands back `"<p></p>"` — truthy, seven characters, no text. A bare
 * `|| null` therefore stores markup for a field the brand just emptied, and because the
 * app decides whether to show an Experience row by asking whether the html contains
 * TEXT (not whether the column is non-null), the stored value and the rendered row
 * disagree: the column looks set, the screen is blank, and the row is still offered.
 *
 * So strip the tags and ask whether anything is left. `&nbsp;` is included because an
 * editor left with a stray non-breaking space is the other way to arrive at visually
 * empty content.
 *
 * The same question is asked on the render side by `customFieldHtmlHasText`
 * (`apps/app/src/utils/certificateHtml.ts`), which sanitizes first. This cannot import
 * that — different app, and it would pull DOMPurify into the platform bundle for one
 * predicate — so the two are deliberately independent and both deliberately
 * conservative: anything either one calls empty is stored as, or rendered as, nothing.
 */
const experienceContentForSave = (raw?: string | null): string | null => {
  if (!raw) return null;
  const text = raw
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return text.length > 0 ? raw : null;
};

const sectionById = (id: string): EditSection =>
  SECTIONS.find((s) => s.id === id) as EditSection;

/**
 * The sections the form actually offers, which is `SECTIONS` minus anything switched off.
 *
 * `SECTIONS` stays whole so `sectionById` keeps resolving every card's Collapse gate —
 * dropping an entry from it would make that lookup return `undefined` and throw inside
 * `isSectionVisible`, which is a crash rather than a hidden card. This filtered list is
 * what the left TOC and the field search read, so a disabled section is absent from both
 * without the gate losing its subject.
 */
const FORM_SECTIONS: EditSection[] = SECTIONS.filter(
  (s) => s.id !== "experience" || EXPERIENCE_ENABLED,
);

// Edit-mode only: the default tag is a relational association set via its own
// endpoint (SetDefaultTagModal), and a product must exist first — so it isn't
// shown in create mode. No form `fields` (it's not part of the product Save).
const DEFAULT_TAG_SECTION: EditSection = {
  id: "defaulttag",
  label: "Default tag",
  fields: [],
  keywords: ["default", "tag", "shop", "ordering"],
};

/**
 * VRG-104 — edit-mode only, for the same reason as the default tag: custom fields save
 * through their own endpoint against an existing product, so there is nothing to curate
 * until the product exists. No form `fields` (the card is not part of the product Save),
 * so it is reachable by the TOC and the search filter on `keywords` alone.
 *
 * Without this the card mounted OUTSIDE the section machinery — visible on the page but
 * absent from the left TOC and unaffected by the filter, which is only invisible while
 * the card itself is gated off. §30: a field on this form has to be reachable from the
 * TOC and the filter, not just present in the DOM.
 */
/**
 * VRG-788 — which STANDARD certificate fields this SKU hides. Edit-mode only for the
 * same reason as the two sections below it: it saves through its own endpoint against an
 * existing product.
 */
const CERTIFICATE_FIELDS_SECTION: EditSection = {
  id: "certificatefields",
  label: "Certificate visibility",
  fields: [],
  keywords: ["certificate", "fields", "hide", "hidden", "visible", "show", "sku", "description"],
};

const CUSTOM_FIELDS_SECTION: EditSection = {
  id: "customfields",
  label: "Custom fields",
  fields: [],
  keywords: ["custom", "fields", "field", "attribute", "attributes", "extra", "certificate"],
};

// VRG-720 — claim on scan. Edit-mode and platform_admin only (a brand admin cannot be granted it
// today, so it is HIDDEN for them rather than disabled with a tooltip: there is no one to ask,
// working-conventions §27). Kept apart from `SECTIONS` for the same reason DEFAULT_TAG_SECTION is:
// that array is module-level and unconditional, while this section exists for one kind of user.
const CLAIM_ON_SCAN_SECTION: EditSection = {
  id: "claim",
  label: "Claim on scan",
  fields: ["claim_on_scan"],
  keywords: ["claim", "scan", "owner", "ownership", "business", "card", "first", "tap"],
};

const TYPE_OPTIONS = CATEGORY_TYPES
  .map((c) => ({ value: c, label: CATEGORY_DISPLAY_NAMES[c] || c, icon: getCategoryIcon(c) }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Legacy/seed type strings → canonical CATEGORY_TYPES, so a product saved with an
// old value (e.g. "jewellery") resolves to the real option instead of rendering a
// duplicate. Saving then writes the canonical value (incrementally fixes data; see
// VRG-74). Unmapped legacy values pass through (Select shows blank until normalized).
const LEGACY_TYPE_MAP: Record<string, string> = {
  jewellery: "jewelry",
  bags: "handbags",
  glasses: "eyewear",
  clothes: "apparel",
};
const normalizeType = (t?: string | null) => (t ? LEGACY_TYPE_MAP[t] || t : "");

const COLUMN_WIDTH = 720;

export const ProductEditPage = ({ page = "edit" }) => {
  // Product URL placeholder: use the brand's manual install location (if set up)
  // with /products appended, otherwise a generic example.
  const { data: currentInstallation } = useCurrentInstallation();
  const productUrlPlaceholder = currentInstallation?.installation_url
    ? `${currentInstallation.installation_url.replace(/\/+$/, "")}/products`
    : "https://example.com/products/product-name";

  const [available, setAvailable] = useState(true);
  // The import notice is an acknowledgement, not a persistent property of the
  // product — once it has been read it is just a band between the header and the
  // first field, on a form the user still has to scroll.
  const [importNoticeOpen, setImportNoticeOpen] = useState(true);
  const [images, setImages] = useState<any>([]);
  /*
    Stays 1, deliberately, and it is NOT the fix for the 0-renders-as-1 bug —
    that one is the `?? 0` in the data effect below.

    This value does double duty, which is the trap. On EDIT it is a pre-load
    placeholder the effect immediately overwrites, so it could be anything. On
    CREATE the effect never runs (`if (data)`), so this IS the starting
    inventory a new product is offered. Changing it to 0 "for consistency"
    silently moves that default, which is a product decision nobody asked for.

    ⚠️ Known, NOT fixed here: on create the form's `quantity` stays undefined
    until the stepper is touched, so the displayed 1 and the submitted value can
    disagree on a create exactly as they used to on edit. Different bug, same
    shape; raised rather than folded into a fix for the reported one.
  */
  const [quantity, setQuantity] = useState(1);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);
  // After an explicit TOC click we pin the active section and ignore scroll-spy
  // until the user scrolls by hand — otherwise clicking a tall/near-bottom
  // section (which clamps the page to the bottom) would let the spy re-pick the
  // last section. Cleared on a genuine wheel/touch/key gesture.
  const suppressSpyRef = useRef(false);

  const { id } = useParams();

  // VRG-353 — values read from a pasted listing URL, handed over by AddModal in
  // router state. Present only on the create route, and only for that flow.
  const location = useLocation();
  const importedProduct = (location.state as any)?.importedProduct as
    | ImportProductFromUrlResponse
    | undefined;

  // Seeded at INIT rather than with setValue after mount, and that distinction
  // is load-bearing. The text fields render as an uncontrolled MUI input with
  // `defaultValue`, so a later setValue updates react-hook-form's state while
  // the DOM input keeps showing empty — which is exactly what happened: the
  // controlled fields (description, availability) filled in and `title` /
  // `product_url` silently did not. defaultValues is read once, before any
  // field mounts, so every field type picks it up.
  const importSeed = useMemo(() => {
    if (page !== "create" || !importedProduct) return undefined;
    return {
      ...(importedProduct.title ? { title: importedProduct.title } : {}),
      ...(importedProduct.description ? { description: importedProduct.description } : {}),
      ...(importedProduct.sku ? { sku: importedProduct.sku } : {}),
      ...(importedProduct.productUrl ? { product_url: importedProduct.productUrl } : {}),
      ...(importedProduct.images?.length ? { images: importedProduct.images } : {}),
    };
    // Empty deps on purpose: location.state is fixed for the life of this
    // mount, and recomputing would fight the user's edits.
  }, []);

  // Required fields a listing page may not supply. `manufacture_date` never is;
  // `sku` often is (JSON-LD usually carries one), so it is checked rather than
  // assumed.
  const missingAfterImport = useMemo(() => {
    if (!importedProduct?.extracted) return [];
    return [!importedProduct.sku ? "a SKU" : null, "a manufacture date"].filter(
      Boolean,
    ) as string[];
    // Empty deps on purpose — same reasoning as importSeed above.
  }, []);

  // Explicit generic, and it is required rather than cosmetic: with
  // `defaultValues` present and no type argument, react-hook-form INFERS the
  // field-name union from the seed's keys alone, so every field the seed does
  // not mention (`type`, `quantity`, `manufacture_date`, `sku`, …) stops being
  // a valid name and the file fails to compile.
  const methods = useForm<ProductFormValues>({
    mode: "onTouched",
    defaultValues: importSeed,
  });
  const {
    handleSubmit,
    formState: { dirtyFields, errors },
    setValue: setFormValue,
  } = methods;
  const navigate = useNavigate();
  const { open: notification } = useSnackbar();

  const { data, isLoading, refetch } = useProduct(
    { productId: id as string },
    {
      enabled: !!id, // Only make the API call if id exists
    }
  );

  // VRG-103 AC 5 — what the next Shopify change will do to an edit made here.
  //
  // Only fetched for a Shopify-synced product being edited; the connection flag is
  // what `sync_mode: 'inherit'` defers to, so without it the 'inherit' case cannot be
  // answered. Gated so a manual or non-Shopify product never triggers the request.
  const isShopifySynced = page === "edit" && data?.integration_provider === "shopify";
  const { data: shopifyConfig } = useFieldMappingConfig("shopify", isShopifySynced);

  const syncNotice = useMemo(() => {
    const NEVER_REACHES_STORE =
      "Edits here do not reach Shopify. To change the product in your store, edit it in Shopify.";

    // `inherit` is the default, and it defers to the connection's "Sync value
    // updates" setting — so the same product gives a different answer depending on a
    // switch that lives on another page. Resolve it here rather than making the user
    // hold both facts in their head.
    const mode = data?.sync_mode ?? "inherit";
    const connectionSyncOn = shopifyConfig?.sync_value_updates_enabled ?? false;
    const willBeOverwritten = mode === "always_sync" || (mode === "inherit" && connectionSyncOn);

    if (willBeOverwritten) {
      // A warning, not an info note: the user is about to spend effort on something
      // that a change in Shopify will silently discard. Naming the escape hatch is
      // the point — telling someone their work is at risk without saying how to
      // protect it is a dead end.
      //
      // VRG-103: the copy used to send the user to "the product page" to change the
      // sync setting — which meant navigating away and losing whatever they'd typed
      // here. The mode control now sits on this same alert (see the Alert `button`
      // slot below), so the copy names the fix without sending anyone anywhere.
      return {
        severity: "warning" as const,
        title: "Shopify will overwrite these edits",
        description:
          mode === "always_sync"
            ? `${NEVER_REACHES_STORE} This product is set to always sync, so the next change in Shopify replaces what you save here. To keep your edits, set its sync to Never sync.`
            : `${NEVER_REACHES_STORE} Sync value updates is on for this store, so the next change in Shopify replaces what you save here. To keep your edits, set this product's sync to Never sync.`,
      };
    }

    return {
      severity: "info" as const,
      title: "Synced from Shopify",
      description:
        mode === "locked"
          ? `${NEVER_REACHES_STORE} This product is locked, so changes in Shopify will not overwrite what you save here.`
          : `${NEVER_REACHES_STORE} Sync value updates is off for this store, so changes in Shopify will not overwrite what you save here.`,
    };
  }, [data?.sync_mode, shopifyConfig?.sync_value_updates_enabled]);

  // VRG-103 — the sync-mode control inline in the Shopify warning alert below.
  // Reuses the same setSyncMode path ProductSyncButton's caret calls (productApi.ts,
  // useProductSyncMode.ts). The mutation's own onSuccess already invalidates the
  // `products` query, but this edit page also has its own `refetch` in scope
  // (matches the explicit refetch ProductPage's caret does) so the alert updates
  // in place immediately rather than waiting on cache invalidation to land.
  const { mutate: setSyncMode } = useProductSyncMode();

  const [openDefaultTagModal, setOpenDefaultTagModal] = useState(false);

  // VRG-720 — claim on scan is staff-only. The RAW role name from WorkOS, not
  // `useAuth().profile.role.name` (which maps unlisted roles to a company member), and not
  // `useIsInternalUser()` (false while a staffer is inside a brand's space, which is exactly
  // where a platform_admin edits a brand's product). The API independently refuses anyone else.
  const { user: authUser } = useWorkOSAuth();
  const isPlatformAdmin = authUser?.role?.name === "platform_admin";
  const [confirmClaimOnScan, setConfirmClaimOnScan] = useState(false);

  // Default-tag, curation and claim-on-scan sections only exist in edit mode (a product
  // must exist first).
  //
  // FORM_SECTIONS, never SECTIONS: it is the Experience-filtered list (VRG-787). Main's
  // side of this conflict spread SECTIONS because main has no such filter, and taking
  // that verbatim would put the hidden Experience card back in the TOC and the search
  // filter while the card itself stays gated off — an outline entry that scroll-spies to
  // nothing.
  const sections =
    page === "edit"
      ? [
          ...FORM_SECTIONS,
          DEFAULT_TAG_SECTION,
          ...(CUSTOM_FIELDS_ENABLED
            ? [CUSTOM_FIELDS_SECTION, CERTIFICATE_FIELDS_SECTION]
            : []),
          // Last, so the staff-only card never shifts anything a brand user sees (and the
          // skeleton, which stops at the default tag, stays a faithful footprint).
          ...(isPlatformAdmin ? [CLAIM_ON_SCAN_SECTION] : []),
        ]
      : FORM_SECTIONS;

  const { setValue, reset } = methods;

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 0) {
      setQuantity(newQuantity);
      setFormValue("quantity", newQuantity.toString());
    }
  };

  const handleIncrement = () => {
    handleQuantityChange(quantity + 1);
  };

  const handleDecrement = () => {
    handleQuantityChange(quantity - 1);
  };

  // VRG-353 — seed the create form from a URL import. Deliberately runs once,
  // and only in create mode: this is a starting point the user edits, so
  // re-applying it would fight their corrections on every re-render.
  //
  // `sku` and `manufacture_date` are both required by the form and a listing
  // page reliably supplies neither, so they are expected to stay blank here.
  // That is the intended behaviour, not a gap — the user fills them in review.
  // The form FIELDS are seeded by `importSeed` above. This effect only syncs the
  // two pieces of state the payload reads from outside react-hook-form
  // (`available` and `images` — see onValid), which defaultValues cannot reach.
  const seedAppliedRef = useRef(false);
  useEffect(() => {
    if (page !== "create" || !importedProduct || seedAppliedRef.current) return;
    seedAppliedRef.current = true;

    if (typeof importedProduct.available === "boolean") {
      setAvailable(importedProduct.available);
    }

    // Images arrive already copied into Vera storage, so they are seeded as
    // EXISTING uploads (URL strings). buildFormData maps a string entry to
    // `existingImages` rather than a multipart part, so nothing is re-uploaded.
    if (importedProduct.images?.length) {
      setImages(importedProduct.images);
    }

    // Validate the SEEDED fields immediately. The form runs `mode: "onTouched"`,
    // which is right for fields a person typed but wrong for values that arrived
    // pre-filled: an imported title over the 32-character limit left Save
    // disabled with NO error on screen, because the field had never been
    // touched. The button looked broken rather than blocked. Validating what we
    // seeded — and nothing else — surfaces the problem without lighting up every
    // empty required field the moment the page opens.
    const seeded = Object.keys(importSeed ?? {}) as (keyof ProductFormValues)[];
    if (seeded.length) void methods.trigger(seeded as any);
  }, [page, importedProduct, importSeed, methods]);

  // `reset`, NOT a run of `setValue` calls — and the difference is the whole reason
  // Save-until-dirty did not work here.
  //
  // `defaultValues` is `importSeed`, which is undefined in edit mode, so the form's
  // baseline was EMPTY. Pouring the loaded record in with setValue changes the values
  // without moving that baseline, so react-hook-form compared a fully-populated form
  // against nothing and reported `isDirty === true` from the moment the record
  // arrived. The `disabled={page === "edit" && !isDirty}` gate on Save was therefore
  // dead code: measured in the browser, Save was enabled on a form nobody had typed
  // in. `reset` makes the loaded record the new baseline, so the form is genuinely
  // pristine until edited (working-conventions §52).
  //
  // keepDefaultValues is deliberately NOT set — replacing the baseline is the point.
  useEffect(() => {
    if (data) {
      reset({
        title: data?.title,
        description: data?.description,
        // Guard the Date construction: `new Date(undefined)` is Invalid Date, which
        // then reads as a change against a null baseline on every load.
        manufacture_date: data?.manufactured_at ? new Date(data.manufactured_at) : null,
        sku: data?.sku,
        type: normalizeType(data?.type),
        quantity: data?.quantity,
        product_url: data?.product_url,
        // `images` must be in the baseline even though the SUBMIT reads local state
        // (see onValid) — the uploader's Controller writes the product's existing
        // images into the form on mount, and against a baseline with no `images` key
        // that registers as a user change. Measured: `dirtyFields` came back as
        // exactly `{ images: true }`, which is what kept Save enabled on an untouched
        // form even after the rest of the record was reset properly.
        images: data?.images ?? [],
        // VRG-323 — null (no per-product override) reads as an empty field, not the
        // string "null"; the company default applies until something is typed here.
        //
        // These two arrived on main as setValue calls while this effect was being
        // converted to reset(). They MUST be listed here: reset() replaces the whole
        // form state, so a field left out is not merely un-seeded, it is wiped —
        // the scan fields would render empty on every load and a Save would clear
        // them. Any field added to this form from now on belongs in this object.
        scan_instructions: data?.scan_instructions ?? "",
        // `?? ""` like the others: the editor's empty value is "", so a null baseline
        // would read as a change the moment the editor mounts and leave Save enabled on
        // an untouched form (working-conventions §54, measured on this very page).
        experience_content: (data as any)?.experience_content ?? "",
        scan_media_url: data?.scan_media_url ?? null,
        // VRG-720 — in the baseline for the same reason as every field above: reset()
        // replaces the whole form state, so a field left out is wiped, not merely unseeded.
        claim_on_scan: data?.claim_on_scan ?? false,
        // VRG-720 — every key present so the baseline matches what the fields render; a key left
        // out would read as a change the moment its field mounts (§54).
        contact_card: {
          name: data?.contact_card?.name ?? "",
          title: data?.contact_card?.title ?? "",
          company: data?.contact_card?.company ?? "",
          phone: data?.contact_card?.phone ?? "",
          email: data?.contact_card?.email ?? "",
          website: data?.contact_card?.website ?? "",
          photo_url: data?.contact_card?.photo_url ?? null,
          socials: (data?.contact_card?.socials ?? []).map((s) => ({ network: s.network, url: s.url })),
        },
        // VRG-749 — in the baseline for the reason the two above record: reset()
        // replaces the whole form state, so a field left out is WIPED rather than
        // merely un-seeded, and a Save would clear the model on every edit.
        model_url: data?.model_url ?? null,
        // VRG-790 — in the baseline for the reason the fields above record: reset()
        // replaces the whole form state, so a field left out is WIPED rather than merely
        // un-seeded, and a Save would clear a brand's reveal on every unrelated edit.
        reveal_video_url: data?.reveal_video_url ?? null,
        reveal_poster_url: data?.reveal_poster_url ?? null,
        reveal_enabled: data?.reveal_enabled ?? false,
      });
      /*
        `?? 0`, NOT `|| 1`. THE BUG Spencer reported: "Quantity shows 0 on the
        products page even when the product's edit page quantity is set to 1 or 2".

        The list was never wrong — it renders the stored value, and four products
        in the preview DB genuinely hold 0. The lie was here. `|| 1` is falsy-
        coalescing, so a stored 0 became a displayed 1, while `reset()` above
        seeded the FORM with the true 0. That split produced both halves of the
        report:

          - open a 0-quantity product -> the stepper reads 1, the form holds 0.
            Save without touching it and 0 is written, so the edit page looks
            like it refuses to save.
          - click "+" once from that displayed 1 -> `handleQuantityChange(1 + 1)`
            writes 2, skipping 1 entirely. Hence "set to 1 or 2" while the list
            still said 0.

        `??` coalesces on null/undefined only, so a real 0 survives and only a
        genuinely absent value falls back.

        And 0 is the right fallback rather than a blank: the column is
        `integer NULL DEFAULT 0` and the live table holds ZERO nulls, so
        "not yet entered" is not a state that occurs. For inventory it is also
        the most actionable value there is — out of stock — which a blank would
        hide. Same reasoning `InfoItem` already records for its own empty check:
        0 is a VALUE, not an absence.
      */
      setQuantity(data?.quantity ?? 0);
      setAvailable(data?.available);
    }
  }, [data, reset]);

  // Whether the form has REAL changes (working-conventions §52).
  //
  // `formState.isDirty` cannot be used directly here, and the reason is worth keeping:
  // the image uploader's Controller seeds itself on mount with objects shaped
  // `{id, file, preview_url, isUploaded}` built from `data.images`, which is a plain
  // array of URL STRINGS — and `id` is generated fresh each mount. So `images` can
  // never equal its own baseline no matter what that baseline is set to, and RHF
  // reported `dirtyFields: { images: true }` on an untouched form forever. That is
  // what left Save enabled on load despite the gate looking correct.
  //
  // So: ignore `images` in RHF's accounting and compare the images ourselves, against
  // the same local state the submit actually reads (see onValid). A genuine add or
  // remove still enables Save; merely opening the page does not.
  const fieldsDirty = Object.keys(dirtyFields).some((k) => k !== "images");

  const imagesDirty = useMemo(() => {
    const original: string[] = data?.images ?? [];
    const current: string[] = (images ?? []).map((i: any) =>
      typeof i === "string" ? i : i?.preview_url,
    );
    if (original.length !== current.length) return true;
    return original.some((url, i) => url !== current[i]);
  }, [data?.images, images]);

  const isDirty = fieldsDirty || imagesDirty;

  const onCancel = () => {
    navigate(-1);
  };

  // ---- Filter: which sections are visible for the current search ----------
  // Safeguard 1: a section with a validation error is NEVER hidden, so the user
  // can always see why Save is blocked.
  const q = search.trim().toLowerCase();
  const isSectionVisible = useMemo(
    () => (section: EditSection) => {
      if (!q) return true;
      const hasError = section.fields.some((f) => Boolean((errors as any)?.[f]));
      if (hasError) return true;
      return (
        section.label.toLowerCase().includes(q) ||
        section.keywords.some((k) => k.includes(q) || q.includes(k))
      );
    },
    [q, errors]
  );

  const visibleSections = sections.filter(isSectionVisible);

  // Validation-error helpers — drive the disabled Save + the TOC error markers.
  const hasErrors = Object.keys(errors).length > 0;

  // Human names for whatever is currently blocking a save, for the Save/Create
  // tooltip. Ordered by SECTIONS so the list reads top-to-bottom in the same
  // order the user would scroll through them.
  const outstandingFields = useMemo(() => {
    const labels: Record<string, string> = {
      images: "product images",
      title: "product name",
      type: "type",
      description: "description",
      manufacture_date: "manufacture date",
      sku: "SKU",
      serialNumber: "serial number",
      product_url: "product URL",
      available: "availability",
      quantity: "quantity",
      scan_instructions: "scan instructions",
      experience_content: "experience",
      scan_media_url: "scan photo or video",
      contact_card: "contact card",
      model_url: "3D model",
      reveal_video_url: "reveal video",
      reveal_poster_url: "reveal poster frame",
      reveal_enabled: "reveal",
    };
    return SECTIONS.flatMap((s) => s.fields)
      .filter((f) => Boolean((errors as Record<string, unknown>)[f]))
      .map((f) => labels[f] ?? f);
  }, [errors]);
  const sectionHasError = (section: EditSection) =>
    section.fields.some((f) => Boolean((errors as any)[f]));

  // ---- Scroll-spy: highlight the TOC entry of the section in view ----------
  // A scroll listener (not IntersectionObserver) so the LAST section can become
  // active even when it's short and the page bottom-clamps before it reaches the
  // top — the observer-band approach can't select an item the page can't scroll
  // far enough to surface.
  useEffect(() => {
    if (isLoading) return;
    const first = sections.map((s) => document.getElementById(s.id)).find(Boolean);
    const sc = getScrollParent(first as HTMLElement | null);
    if (!sc) return;

    const onScroll = () => {
      if (suppressSpyRef.current) return;
      const present = sections.filter((s) => document.getElementById(s.id));
      if (!present.length) return;
      // At the bottom, force the last section active (it may never reach the top).
      if (sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 4) {
        setActiveId(present[present.length - 1].id);
        return;
      }
      const scTop = sc.getBoundingClientRect().top;
      // 100 is layout px; the rect delta is painted px (× the text zoom — see `zoomOf`).
      const scZoom = zoomOf(sc);
      let current = present[0].id;
      for (const s of present) {
        const el = document.getElementById(s.id)!;
        if (el.getBoundingClientRect().top - scTop <= 100 * scZoom) current = s.id;
      }
      setActiveId(current);
    };

    // A real user gesture re-enables scroll-spy (clears the post-click pin).
    const releaseSpy = () => {
      suppressSpyRef.current = false;
    };

    sc.addEventListener("scroll", onScroll, { passive: true });
    sc.addEventListener("wheel", releaseSpy, { passive: true });
    sc.addEventListener("touchmove", releaseSpy, { passive: true });
    sc.addEventListener("keydown", releaseSpy);
    onScroll();
    return () => {
      sc.removeEventListener("scroll", onScroll);
      sc.removeEventListener("wheel", releaseSpy);
      sc.removeEventListener("touchmove", releaseSpy);
      sc.removeEventListener("keydown", releaseSpy);
    };
  }, [isLoading, search]);

  // Nearest scrollable ancestor (the app scrolls inside <main>, not window).
  const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
    let el = node?.parentElement ?? null;
    while (el) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  };

  const scrollToSection = (sectionId: string) => {
    suppressSpyRef.current = true; // pin until the user scrolls by hand
    setActiveId(sectionId);
    const el = document.getElementById(sectionId);
    if (!el) return;
    const sc = getScrollParent(el);
    if (!sc) {
      el.scrollIntoView({ block: "start", behavior: "smooth" });
      return;
    }
    // If a sticky top nav overlays the scroll-container top (small screens), land the
    // section just below it. On wide screens the nav is a side rail (not sticky at the
    // container top) so this resolves to 0 — only the 12px breathing room applies.
    let stickyOffset = 0;
    for (
      let n = document.querySelector<HTMLElement>("[data-section-id]");
      n && n !== sc;
      n = n.parentElement
    ) {
      if (getComputedStyle(n).position === "sticky") {
        // Distance from the container top to the sticky nav's bottom edge — the exact
        // amount to clear so the section lands fully below the nav (more reliable than
        // the nav's box height when it isn't flush at the container top).
        stickyOffset =
          Math.max(0, n.getBoundingClientRect().bottom - sc.getBoundingClientRect().top) /
          zoomOf(sc);
        break;
      }
    }
    // 12px breathing room above the card (plus any sticky-nav height).
    // `scrollTop` is LAYOUT px; rect deltas are PAINTED px (× the text zoom). Both the
    // delta and the sticky offset above are divided back to layout px before they are
    // added to it, or the section lands short by delta × (1 − z) at z < 1.
    const delta = (el.getBoundingClientRect().top - sc.getBoundingClientRect().top) / zoomOf(sc);
    const target = Math.max(
      0,
      Math.min(
        sc.scrollTop + delta - 12 - stickyOffset,
        sc.scrollHeight - sc.clientHeight
      )
    );
    // Smooth-scroll via a rAF tween rather than native scrollTo({smooth}) — native
    // smooth is a no-op inside this app's nested <main> in some webviews, and the old
    // code's instant `scrollTop = target` fallback ran alongside it, cancelling the
    // animation (the "flash"). This animates scrollTop directly (works everywhere),
    // with a timeout fallback that lands the scroll if rAF is throttled.
    const start = sc.scrollTop;
    const change = target - start;
    if (Math.abs(change) < 1) return;
    const t0 = performance.now();
    const ease = (t: number) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    let done = false;
    const step = (now: number) => {
      if (done) return;
      const p = Math.min(1, (now - t0) / 320);
      sc.scrollTop = start + change * ease(p);
      if (p < 1) requestAnimationFrame(step);
      else done = true;
    };
    requestAnimationFrame(step);
    setTimeout(() => {
      if (!done && sc.scrollTop === start) {
        sc.scrollTop = target;
        done = true;
      }
    }, 80);
  };

  // TOC click: if the target section is currently filtered out, clear the search
  // first so it un-hides, then scroll to it on the next frame.
  const handleTocChange = (sectionId: string) => {
    suppressSpyRef.current = true;
    const section = sections.find((s) => s.id === sectionId);
    if (section && !isSectionVisible(section)) {
      setSearch("");
      setActiveId(sectionId);
      setTimeout(() => scrollToSection(sectionId), 50);
      return;
    }
    scrollToSection(sectionId);
  };

  // ---- Submit -------------------------------------------------------------
  // VRG-719 — a failed save that the API attributes to a FIELD (`details: {[field]:
  // string[]}`: a duplicate SKU, an image the server could not store, a DTO rule) is shown
  // under that field and scrolled to, exactly like a client-side validation error. Only a
  // failure that names no field this form has falls back to the toast.
  //
  // Before this, every save failure was one toast built from `getErrorMessage`, and on
  // prod (2026-10-17, 2026-10-19) the only text on offer was an internal bucket path, which
  // the toast filter rightly replaced with "Something went wrong". The reader learned
  // neither WHAT failed nor WHERE.
  const handleSaveError = (error: unknown) => {
    const formFields = new Set(SECTIONS.flatMap((s) => s.fields));
    const fieldErrors = serverFieldErrors(error, {
      manufactured_at: "manufacture_date",
      // The API attributes contact card failures as `contact_card.<field>`, which is also the
      // form's own field path (VRG-720).
    }).filter(
      (e) => formFields.has(e.field) || e.field.startsWith("contact_card."),
    );

    if (!fieldErrors.length) {
      notification({ type: "error", title: getErrorMessage(error) });
      return;
    }

    fieldErrors.forEach(({ field, message }) =>
      methods.setError(field as keyof ProductFormValues, { type: "server", message })
    );
    // Same recovery as a client-side invalid submit: clear any filter hiding the field and
    // bring the first one into view.
    // Keyed by the top-level field the section owns (`contact_card.email` -> `contact_card`).
    onInvalid(Object.fromEntries(fieldErrors.map((e) => [e.field.split(".")[0], true])));
  };

  const [{ create }, { isLoading: isCreating }] = useProductMutations({
    // No toast: the Products list we land on shows the new row.
    onSuccess: () => {
      navigate(ROUTES.PRODUCTS);
    },
    onError: handleSaveError,
  });

  const [{ update }, { isLoading: isUpdating }] = useProductMutations({
    onSuccess: (data) => {
      navigate(ROUTES.PRODUCT(data?.id as string));
    },
    onError: handleSaveError,
  });

  const onValid = (data: any) => {
    if (page === "create") {
      create({
        title: data?.title,
        description: data?.description,
        manufactured_at: data?.manufacture_date,
        sku: data?.sku,
        available: available,
        images: images,
        quantity: data?.quantity,
        // Blank omitted rather than sent as "": `null` is the canonical empty for
        // this column everywhere else that writes it (bulk CSV import writes null,
        // the Shopify mapper omits the key), so "" would be a value no other path
        // produces. On CREATE there is nothing to clear, so undefined is enough.
        product_url: data?.product_url?.trim() || undefined,
        type: data?.type || undefined,
        // VRG-323 — `|| undefined` so a blank field is omitted rather than sent as
        // "", which would write an empty override and suppress the company default.
        scan_instructions: data?.scan_instructions || undefined,
        // VRG-782 — same `|| undefined` as its neighbours: an empty editor yields
        // "<p></p>" or "", neither of which should be written as a value. See the note
        // on the update branch for why the UPDATE sends null instead.
        experience_content: experienceContentForSave(data?.experience_content) ?? undefined,
        scan_media_url: data?.scan_media_url || undefined,
        // VRG-720 — omitted when nothing was entered (on CREATE there is nothing to clear).
        contact_card: buildContactCard(data?.contact_card) ?? undefined,
        // VRG-749 — `|| undefined` on CREATE: there is nothing to clear yet, so an
        // unset field is simply omitted.
        model_url: data?.model_url || undefined,
        // VRG-790 — `|| undefined` on CREATE for the model's reason: there is nothing to
        // clear yet, so an unset field is simply omitted.
        reveal_video_url: data?.reveal_video_url || undefined,
        reveal_poster_url: data?.reveal_poster_url || undefined,
        // Sent only when ON. `false` is the column default, so omitting it says the same
        // thing, and a create form has no stored value to override.
        ...(data?.reveal_enabled ? { reveal_enabled: true } : {}),
      });
    } else {
      update({
        id: id as string,
        title: data?.title,
        description: data?.description,
        manufactured_at: data?.manufacture_date,
        available: available,
        quantity: data?.quantity,
        images: images,
        // `null`, NOT undefined or "", when the user empties the field. undefined is
        // dropped by JSON.stringify in buildFormData, so clearing would be impossible.
        // "" would persist a value no other writer produces, and — the reason it
        // matters here rather than being mere tidiness — the re-import guard in
        // AddProductsFromIntegrationUseCase tests `=== undefined || === null` exactly,
        // so "" is the one empty that reads as a real value to it.
        product_url: data?.product_url?.trim() || null,
        type: data?.type || undefined,
        // VRG-323 — `null`, NOT undefined, when the user empties the field:
        // buildFormData JSON.stringifies the payload and undefined keys are
        // dropped, so omitting them would make clearing an override impossible.
        // null clears it and the company-wide default applies again.
        scan_instructions: data?.scan_instructions?.trim() || null,
        // `null`, not undefined or "", for the reason the block above gives: undefined
        // is dropped by buildFormData so clearing would be impossible, and the API's
        // re-import guard tests `=== undefined || === null` exactly.
        //
        // Routed through `experienceContentForSave` because the rich-text editor does
        // not produce "" when emptied — it produces "<p></p>", which is truthy, and a
        // bare `|| null` would persist markup-with-no-text. That value is exactly what
        // would put an Experience row on every certificate for this SKU leading to a
        // blank screen, since the app gates the row on text rather than on the column
        // being non-null.
        experience_content: experienceContentForSave(data?.experience_content),
        scan_media_url: data?.scan_media_url || null,
        // VRG-720 — `null`, not undefined, when emptied: JSON.stringify drops undefined, so
        // `null` is the only way to clear a stored card.
        contact_card: buildContactCard(data?.contact_card),
        // VRG-720 — only a platform_admin names the field at all. Anyone else sending it would be
        // refused (403) the moment it differed from the stored value, and their form never held
        // a value they chose, so it is omitted rather than restated.
        ...(isPlatformAdmin ? { claim_on_scan: Boolean(data?.claim_on_scan) } : {}),
        // VRG-749 — `null`, NOT undefined, when the model is removed: buildFormData
        // JSON.stringifies the payload and undefined keys are dropped, so omitting
        // it would make removing a model impossible — the X would appear to work and
        // the old model would be back on the next load.
        model_url: data?.model_url || null,
        // VRG-790 — `null`, NOT undefined, for the model's reason verbatim: buildFormData
        // JSON.stringifies the payload and undefined keys are dropped, so omitting these
        // would make REMOVING a reveal impossible — the X would appear to work and the
        // old video would be back on the next load.
        reveal_video_url: data?.reveal_video_url || null,
        reveal_poster_url: data?.reveal_poster_url || null,
        // Always sent, and always a real boolean: this is the one field whose FALSE is a
        // meaningful instruction (turn the reveal off), so it must never be omitted.
        reveal_enabled: Boolean(data?.reveal_enabled),
      });
    }
  };

  // Safeguard 2: a failed Save clears the filter and scrolls to the first error,
  // so a hidden invalid field can never silently block Save.
  const onInvalid = (formErrors: any) => {
    setSearch("");
    const firstErrorSection = SECTIONS.find((s) =>
      s.fields.some((f) => Boolean(formErrors?.[f]))
    );
    if (firstErrorSection) {
      setTimeout(() => {
        // VRG-720 — land on the errored FIELD when one is rendered, not the section top.
        // The Contact card section is a photo, six fields and the social rows tall, so at
        // 800px a bad social link sat below the fold after a failed Save: the page moved
        // and still did not show what was wrong.
        const errorEl = document.querySelector(`#${firstErrorSection.id} .Mui-error`);
        if (!errorEl) return scrollToSection(firstErrorSection.id);
        suppressSpyRef.current = true;
        setActiveId(firstErrorSection.id);
        errorEl.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 50);
    }
  };

  const onSubmit = handleSubmit(onValid, onInvalid);

  // Save/Create button state (VRG-365).
  //
  // `saveDisabled` mirrors the existing `!isDirty` gate. `saveDisabledReason`
  // prefers the error list when pre-filled invalid data coexists with a
  // pristine form (see the `methods.trigger(seeded)` effect above), else the
  // plain pristine message.
  const saveDisabled = page === "edit" && !isDirty;
  const saveDisabledReason =
    hasErrors && outstandingFields.length
      ? `Still needed: ${outstandingFields.join(", ")}`
      : "Make a change to save";

  // The Save/Create button itself. Deliberately NOT disabled on `hasErrors`
  // alone — everything needed to explain a validation problem already exists
  // (`onInvalid` clears the search filter and scrolls to the first bad
  // section, the section nav shows an error badge), and letting the click
  // through runs all of it. `!isDirty` on edit stays disabled — that is
  // "nothing to save", not "something is wrong".
  // VRG-719 — in flight, the button is busy (`loading` disables it and blocks the click,
  // §46). It was clickable throughout, so a slow save (a multipart body with several
  // images) could be sent a second time while the first was still running. Repeat clicks
  // AFTER a failure stay possible on purpose: the button is the retry affordance, and the
  // toast stack those produced is handled by the provider's de-duplication instead.
  const isSaving = page === "create" ? isCreating : isUpdating;
  const saveButtonEl = (
    <Button
      disableElevation
      disabled={saveDisabled}
      disabledReason={saveDisabledReason}
      loading={isSaving}
      onClick={onSubmit}
    >
      {page == "edit" ? "Save" : "Create"}
    </Button>
  );

  // The hover-preview of outstanding fields on an ENABLED Save with errors
  // ("you can click, but here's what's still missing") is a SEPARATE Tooltip
  // from the shared Button's own `disabledReason` tooltip — and the two must
  // never wrap the same element at once. Nesting them (an outer Tooltip
  // always present, with an empty `title` when disabled) is what broke real
  // mouse hover/click on the disabled button (VRG-365 follow-up): MUI's
  // Tooltip clones its child and attaches its own mouse/focus listeners
  // regardless of whether `title` is empty, and two Tooltips composing
  // handlers onto the same DOM button raced with the inner one's controlled
  // `open` state. So render AT MOST ONE Tooltip around the button: the
  // shared Button's own (when disabled — `saveDisabled`), or this outer
  // hover-preview (when enabled AND there are errors to preview). Never
  // both.
  const saveButtonNode =
    !saveDisabled && outstandingFields.length ? (
      <Tooltip
        title={`Still needed: ${outstandingFields.join(", ")}`}
        placement="bottom-end"
        // `describeChild` — MUI's default (`false`) sets `aria-label` on the
        // trigger, which REPLACES its accessible name; without it a screen
        // reader would announce "Still needed: …" and never "Save". This
        // preserves "Save" as the name and adds the preview as a description
        // (matches the fix on the shared Button's own disabled tooltip).
        describeChild
      >
        <span>{saveButtonEl}</span>
      </Tooltip>
    ) : (
      saveButtonEl
    );

  const sectionSx = FORM_SECTION_SX;

  return (
    <>
      {isLoading ? (
        // Footprint-parity placeholder (§19) in place of the centred <Loader />, which reserved none
        // of the 200px TOC rail, the 167px sticky header, or the five cards. Only ever reached in
        // EDIT mode — in create mode the product query is disabled, so `isLoading` is false. See
        // ProductEditPageSkeleton for the measurements.
        <ProductEditPageSkeleton sections={sections} onBack={onCancel} />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 6,
              width: "100%",
              // Was mb:16 — clearance for the fixed bottom action bar, which is gone. A small
              // tail so the last card doesn't sit flush on the viewport edge.
              mb: 6,
              justifyContent: "center",
              // Center the form column on small screens too (column layout defaults to
              // align-items:stretch, which — with the column's maxWidth — pinned it left).
              alignItems: { xs: "center", md: "stretch" },
            }}
          >
            {/* Responsive TOC: vertical left rail on md+, horizontal scroll row
                (with Back pinned left) on small screens. */}
            <Box data-tour="product-edit-toc">
              <SectionNav
                items={sections.map((s) => ({
                  label: s.label,
                  value: s.id,
                  endAdornment: sectionHasError(s) ? (
                    <Box component="span" sx={{ color: "error.main", display: "inline-flex" }}>
                      <CircleAlertIcon size={16} strokeWidth={1.5} />
                    </Box>
                  ) : undefined,
                }))}
                value={activeId}
                onChange={handleTocChange}
                onBack={onCancel}
              />
            </Box>

            {/* Main column */}
            <Box
              sx={{
                flex: 1,
                maxWidth: COLUMN_WIDTH,
                // Fill up to maxWidth on small screens so the centered column keeps the
                // full form width; `auto` on md defers to flex:1 in the row layout.
                width: { xs: "100%", md: "auto" },
                display: "flex",
                flexDirection: "column",
                gap: 6,
                // Keep the column from collapsing when search filters everything
                // out — otherwise the stretched left rail (and its sticky TOC)
                // shrinks and the TOC shifts up.
                minHeight: vhMinus(160),
              }}
            >
              <StickyFormHeader
                // Name the RECORD, not the type. "Edit Product" repeats what the
                // breadcrumb above it already says, and spends the largest text on the
                // page saying nothing — while the question a user actually has on an
                // edit form is "am I editing the right one?". Arriving from a list row
                // or a cross-record link, that is a real mis-edit risk, and it also
                // makes five open edit tabs distinguishable from each other.
                //
                // Falls back to "Edit Product" until the record loads (the page renders
                // a full skeleton first, so this is a brief window) and in create mode,
                // where there is no name yet. Header truncates with an ellipsis and
                // exposes the full name on hover.
                title={
                  page === "edit"
                    ? data?.title
                      ? `Edit ${data.title}`
                      : "Edit Product"
                    : "Create Product"
                }
                actions={
                  <>
                    <Button variant="secondary" disableElevation onClick={() => onCancel()}>
                      Cancel
                    </Button>
                    {saveButtonNode}
                  </>
                }
              >
                <Box data-tour="product-edit-search">
                  <Search
                    onChange={(value: string) => setSearch(value)}
                    fullWidth={true}
                    placeholder="Search fields..."
                  />
                </Box>
              </StickyFormHeader>

              {/* VRG-353 — what the URL import managed to read, and what it didn't.
                  Stated up front so the user knows which fields to check rather
                  than assuming a pre-filled form is a complete one. */}
              {page === "create" && importedProduct && importNoticeOpen && (
                <Alert
                  onClose={() => setImportNoticeOpen(false)}
                  severity={importedProduct.extracted ? "info" : "warning"}
                  title={
                    importedProduct.extracted
                      ? "Filled in from your product page"
                      : "Nothing could be read from that page"
                  }
                  description={
                    importedProduct.extracted ? (
                      <>
                        {[
                          "Check everything before saving.",
                          importedProduct.imageWarning,
                          // Name only what is actually still blank. A product
                          // page often does carry a SKU (JSON-LD usually has
                          // one), and telling someone to fill in a field that
                          // is already filled teaches them to ignore this.
                          missingAfterImport.length
                            ? `You'll still need to add ${missingAfterImport.join(" and ")}.`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        {/* VRG-723 — WHICH images, and why each one: whenever only some failed,
                            or they failed for different reasons. When every image failed for one
                            reason (a site that blocks downloads) the sentence above says it all. */}
                        {(importedProduct.imageFailures?.length ?? 0) > 0 &&
                          (importedProduct.images.length > 0 ||
                            new Set(importedProduct.imageFailures!.map((f) => f.reason)).size > 1) && (
                            <Box component="ul" sx={{ m: 0, mt: 1, pl: 4 }} data-testid="import-image-failures">
                              {importedProduct.imageFailures!.map((f) => (
                                <li key={f.url} style={{ wordBreak: "break-all" }}>
                                  {f.url.split("/").pop()?.split("?")[0] || f.url}: {f.reason}
                                </li>
                              ))}
                            </Box>
                          )}
                      </>
                    ) : (
                      "The link was saved, but none of the product details could be read. Fill the form in yourself."
                    )
                  }
                />
              )}

              {/* Shopify Integration Warning — VRG-103 AC 5.
                  Two facts, and the second one is new. "Your edit will not reach
                  Shopify" was always true. "The next Shopify change may overwrite your
                  edit" only became true when the products/update webhook shipped, so
                  this alert now has to say which of the two futures applies to THIS
                  product, per its sync_mode.

                  The alert's own `button` slot carries the fix INLINE — the old copy
                  told the user to go change the setting "from the product page", which
                  meant navigating away and losing whatever was typed here. This reuses
                  `ProductSyncModeMenu` (the same menu ProductSyncButton's caret opens on
                  the product page) so the two controls can never drift — only the mode
                  picker is shown here, not "Sync now" (that action doesn't belong on a
                  form the user hasn't saved yet). Changing the mode invalidates the
                  `products` query, so `syncNotice` recomputes and this alert updates in
                  place (severity + copy) without a page reload. */}
              {page === "edit" && data?.integration_provider === "shopify" && (
                <Alert
                  severity={syncNotice.severity}
                  title={syncNotice.title}
                  description={syncNotice.description}
                  button={
                    <ProductSyncModeMenu
                      mode={data?.sync_mode ?? "inherit"}
                      onChangeMode={(syncMode) =>
                        setSyncMode({ productId: id as string, syncMode }, { onSuccess: () => refetch() })
                      }
                      renderTrigger={({ onClick, current }) => {
                        const CurrentIcon = current.icon;
                        return (
                          // `variant="text"` (borderless), not "secondary" — the Alert's
                          // `button` slot forces every descendant's `color` to the
                          // severity token (`'& *': { color: tokens.buttonColor }`,
                          // Alert.tsx), so a bordered secondary button would end up with
                          // a neutral border around severity-tinted text: an unintentional
                          // mismatch. Borderless reads as "part of the alert" rather than
                          // a separate control fighting the alert's own colour language.
                          <Button
                            variant="text"
                            disableElevation
                            onClick={onClick}
                            aria-label={`Change auto-sync — currently ${current.label}`}
                            startIcon={<CurrentIcon strokeWidth={1} size={14} />}
                            endIcon={<ChevronDownIcon strokeWidth={1} size={14} />}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {current.label}
                          </Button>
                        );
                      }}
                    />
                  }
                />
              )}

              <FormProvider {...methods}>
                {/* MEDIA */}
                <Collapse in={isSectionVisible(sectionById("media"))} timeout={250} unmountOnExit>
                  <Box id="media" sx={sectionSx}>
                    <Card title="Media" info={true}>
                      <Controller
                        name="images"
                        control={methods.control}
                        rules={{
                          validate: (files) => {
                            if (!files || files.length === 0) {
                              return "At least one image is required";
                            }

                            // Size and type checks apply to files the user is
                            // ADDING, never to images Vera already stores.
                            //
                            // An existing image arrives either as a plain URL
                            // string or as `{ isUploaded: true }`, and the File
                            // behind it is rebuilt client-side by re-fetching
                            // the URL — so its `type` is whatever the fetch
                            // reported (empty if the storage response omitted a
                            // content-type) and its `name` is scraped from the
                            // path. Judging an already-stored image on that
                            // reconstruction is fragile and pointless: it passed
                            // these checks when it was uploaded. It produced
                            // "Invalid file types: Unknown file" on every
                            // URL-imported product.
                            const newFiles = files.filter(
                              (file: any) => typeof file !== "string" && !file?.isUploaded
                            );

                            // Check file sizes
                            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
                            const oversizedFiles = newFiles.filter(
                              (file: any) => file.file?.size > maxSize
                            );
                            if (oversizedFiles.length > 0) {
                              return `Files too large: ${oversizedFiles
                                .map((f: any) => f.file?.name || "Unknown file")
                                .join(", ")}. Maximum size is 10MB.`;
                            }

                            /*
                             * VRG-777 — the accepted set now includes VIDEO, and HEIC has
                             * been REMOVED rather than kept.
                             *
                             * HEIC was never actually accepted. The API's own kind table has
                             * always refused it (`upload-kinds.ts` argues why: no browser
                             * renders one, so storing it hands the brand a link nobody can
                             * open), and until VRG-777 that route had NO server-side type
                             * check at all — so a HEIC advertised here was uploaded, stored,
                             * and then failed to render everywhere, with no error anywhere
                             * to explain it. The picker was promising something the stack
                             * cannot deliver, and there is no transcoder in this repo to
                             * make it deliverable. Removing it is the honest fix: the
                             * refusal now arrives at the picker, before the save, instead
                             * of silently afterwards.
                             *
                             * Kept MIME-first with an extension fallback, because a browser
                             * sometimes reports an empty `type` for a dragged file and a
                             * reconstructed one.
                             */
                            const allowedTypes = PRODUCT_MEDIA_ACCEPT_MIME_TYPES;
                            const allowedExtensions = PRODUCT_MEDIA_ACCEPT_EXTENSIONS;

                            const invalidFiles = newFiles.filter((file: any) => {
                              const fileName = file.file?.name?.toLowerCase() || '';
                              const fileType = file.file?.type || '';

                              const hasValidMimeType = allowedTypes.includes(fileType);
                              const hasValidExtension = allowedExtensions.some(ext =>
                                fileName.endsWith(ext)
                              );

                              return !hasValidMimeType && !hasValidExtension;
                            });

                            if (invalidFiles.length > 0) {
                              return `Invalid file types: ${invalidFiles
                                .map((f: any) => f.file?.name || "Unknown file")
                                .join(
                                  ", "
                                )}. ${PRODUCT_MEDIA_ACCEPT_MESSAGE}`;
                            }

                            /*
                             * VRG-777 — A VIDEO MUST BE ACCOMPANIED BY A STILL, and the save
                             * is REFUSED with a reason rather than the video being dropped.
                             *
                             * The still does two jobs nothing else can do:
                             *   - it is the THUMBNAIL. `getProductThumbnail` returns the
                             *     first non-video entry, and every list cell, picker row,
                             *     preview card and the outreach PNG renders that one frame.
                             *     With no still, all of them fall back to a placeholder and
                             *     the product looks like it has no media at all.
                             *   - it is the POSTER frame on the certificate hero, which is
                             *     what stands in while the clip loads and — the case that
                             *     matters — what iOS shows instead when Low Power Mode
                             *     refuses autoplay outright. Without it the hero is a black
                             *     box on a phone with battery saver on.
                             *
                             * It cannot be generated: there is no `ffmpeg`, no `sharp`, and
                             * no poster-frame extraction anywhere in this monorepo, so the
                             * frame can only come from the brand. Validated over the WHOLE
                             * list (`files`), not just `newFiles` — adding a clip to a
                             * product that already has photographs is fine, and removing the
                             * last photograph from a product that has a clip is not.
                             */
                            const entryIsVideo = (entry: any): boolean => {
                              if (typeof entry === "string") return isVideoUrl(entry);
                              const mime = entry?.file?.type ?? "";
                              if (mime) return mime.startsWith("video/");
                              // No MIME (a reconstructed file, or a browser that reported
                              // none): fall back to the name, then to the stored URL.
                              return (
                                isVideoUrl(entry?.file?.name ?? "") ||
                                isVideoUrl(entry?.preview_url ?? "")
                              );
                            };

                            const videos = files.filter(entryIsVideo);
                            if (videos.length > 0 && videos.length === files.length) {
                              return "A video needs a still image alongside it. The still is what appears in product lists and as the frame before the video plays, and it cannot be generated from the video — please add at least one photo.";
                            }

                            return true;
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <FileUploadField
                            label="Product Media"
                            // VRG-777 — READ from the shared declaration, never retyped
                            // here. A hand-written copy of this list is what advertised
                            // HEIC for months while the API refused it; see
                            // `PRODUCT_MEDIA_ACCEPT` and the parity guard that now holds
                            // the two in agreement.
                            acceptFiles={PRODUCT_MEDIA_ACCEPT}
                            // Says the two things a brand cannot discover by trying:
                            // that a clip needs a still beside it (the validate rule
                            // below refuses the save otherwise), and that the audio
                            // track is ignored — so nobody authors for sound and then
                            // wonders why the hero is silent (it must be muted, because
                            // iOS autoplays only muted + inline).
                            description="Accepts JPG, PNG, GIF, WEBP, MP4, MOV, WEBM. Drag to reorder — the first still is the thumbnail. A video plays on the certificate, looping and muted (its audio is ignored), and needs at least one still alongside it."
                            showMaxFileSize={true}
                            // In create mode `data` is null, so a URL import
                            // supplies the initial gallery instead. These are
                            // already Vera-hosted (the API copies them), so
                            // FileUploadField's fetch-to-blob is same-origin.
                            items={data?.images ?? importedProduct?.images}
                            onFilesChange={(files) => {
                              setImages(files);
                              field.onChange(files);
                            }}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                    </Card>
                  </Box>
                </Collapse>

                {/* BASIC INFO */}
                <Collapse in={isSectionVisible(sectionById("basic"))} timeout={250} unmountOnExit>
                  <Box id="basic" sx={sectionSx}>
                    <Card title="Basic info" info={true}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
                          <Box sx={{ flex: { xs: "1 1 100%", md: 2 }, minWidth: 240 }}>
                            <Controller
                              name="title"
                              control={methods.control}
                              rules={PRODUCT_RULES.title}
                              // Wired like CreateOwnerPage's fields, which work.
                              // Do NOT spread `{...field}` here and do NOT pass
                              // `rules` to TextField:
                              //
                              //  - TextField calls `register(name, rules)` itself,
                              //    so passing rules registers the field a SECOND
                              //    time alongside this Controller. On blur RHF then
                              //    re-reads the value through the registration that
                              //    won, finds nothing, and reports "This field is
                              //    required" against a visibly filled input.
                              //  - `{...field}` also passes `value: undefined` on
                              //    first render (leaving the input uncontrolled) and
                              //    an `onChange`/`onBlur` that TextField does not
                              //    destructure, so they are silently dropped.
                              //
                              // An explicit, never-undefined `value` plus
                              // `onValueChange` keeps one registration and one
                              // value path.
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...methods}
                                  label="Product Name"
                                  name="title"
                                  value={field.value == null ? "" : String(field.value)}
                                  onValueChange={(value) => field.onChange(value)}
                                  onFieldBlur={field.onBlur}
                                  helperText={fieldState.error?.message}
                                />
                              )}
                            />
                          </Box>
                          <Box sx={{ flex: { xs: "1 1 100%", md: 1 }, minWidth: 200 }}>
                            <Controller
                              name="type"
                              control={methods.control}
                              render={({ field }) => (
                                <SelectField
                                  label="Type"
                                  search
                                  options={TYPE_OPTIONS}
                                  value={field.value || ""}
                                  onChange={(value) => field.onChange(value)}
                                />
                              )}
                            />
                          </Box>
                        </Box>
                        <Controller
                          name="description"
                          control={methods.control}
                          rules={PRODUCT_RULES.description}
                          render={({ field }) => (
                            <RichTextEditorField
                              label="Description"
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="A short overview of this product…"
                              helperText="A short overview, shown at the top of the product's certificate."
                              minHeight="200px"
                            />
                          )}
                        />
                      </Box>
                    </Card>
                  </Box>
                </Collapse>

                {/* SPECIFICATIONS */}
                <Collapse in={isSectionVisible(sectionById("specs"))} timeout={250} unmountOnExit>
                  <Box id="specs" sx={sectionSx}>
                    <Card title="Specifications">
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
                          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.333% - 16px)" }, minWidth: "200px" }}>
                            <Controller
                              name="manufacture_date"
                              control={methods.control}
                              rules={PRODUCT_RULES.manufacture_date}
                              render={({ field }) => (
                                <DatePickerField
                                  label="Manufacture date"
                                  // Marked the same way Serial Number is, so "no
                                  // asterisk" isn't the only signal that it's optional.
                                  helperText="optional"
                                  defaultValue={data?.manufactured_at}
                                  rules={PRODUCT_RULES.manufacture_date}
                                  onValueChange={(value: string | null) => {
                                    field.onChange(value);
                                  }}
                                  format="MM/DD/YYYY"
                                  disableFuture={true}
                                  views={['year', 'month', 'day']}
                                  {...methods}
                                  {...field}
                                />
                              )}
                            />
                          </Box>

                          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.333% - 16px)" }, minWidth: "200px" }}>
                            <Controller
                              name="sku"
                              control={methods.control}
                              rules={PRODUCT_RULES.sku}
                              // Same single-registration wiring as `title` above.
                              // (`disabled` was passed here previously and did
                              // nothing — TextField destructures `readOnly`, not
                              // `disabled` — so dropping it changes no behaviour.)
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...methods}
                                  label="SKU"
                                  name="sku"
                                  value={field.value == null ? "" : String(field.value)}
                                  onValueChange={(value) => field.onChange(value)}
                                  onFieldBlur={field.onBlur}
                                  helperText={fieldState.error?.message}
                                />
                              )}
                            />
                          </Box>

                          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.333% - 16px)" }, minWidth: "200px" }}>
                            <Controller
                              name="serialNumber"
                              control={methods.control}
                              render={({ field }) => (
                                <TextField
                                  {...methods}
                                  label="Serial Number"
                                  name="serialNumber"
                                  helperText="optional"
                                  value={field.value == null ? "" : String(field.value)}
                                  onValueChange={(value) => field.onChange(value)}
                                  onFieldBlur={field.onBlur}
                                />
                              )}
                            />
                          </Box>
                        </Box>

                        <Controller
                          name="product_url"
                          control={methods.control}
                          rules={PRODUCT_RULES.product_url}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...methods}
                              label="Product URL"
                              name="product_url"
                              type="url"
                              helperText={
                                fieldState.error?.message ??
                                "URL to the original product listing where the product is sold"
                              }
                              placeholder={productUrlPlaceholder}
                              value={field.value == null ? "" : String(field.value)}
                              onValueChange={(value) => {
                                field.onChange(value);
                                // Live validation as you type. The explicit trigger
                                // used to be a workaround for blur not reaching RHF;
                                // with the single-registration wiring above it no
                                // longer is, but it is kept so a malformed URL is
                                // flagged before the field is left.
                                methods.trigger("product_url");
                              }}
                              onFieldBlur={field.onBlur}
                            />
                          )}
                        />

                      </Box>
                    </Card>
                  </Box>
                </Collapse>

                {/* SCAN INSTRUCTIONS (VRG-323) — per-product override of the
                    company-wide default in Settings → Installation. */}
                <Collapse in={isSectionVisible(sectionById("scan"))} timeout={250} unmountOnExit>
                  <Box id="scan" sx={sectionSx}>
                    <Card title="Scan instructions">
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                        {/* lineHeight 1.5 and two sentences rather than one em-dash-joined
                            clause (§15) — this is the only place the fallback is stated, so
                            it has to read plainly. */}
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          Tell a customer where the tag sits on this item. It shows in the
                          "Where to scan?" panel of the app. Leave both blank to use your
                          company default.
                        </Typography>

                        <Controller
                          name="scan_instructions"
                          control={methods.control}
                          // Same single-registration wiring as `title` above: no
                          // `{...field}` spread and no `rules` on TextField. See the
                          // long note there for what each one breaks.
                          render={({ field, fieldState }) => (
                            <TextField
                              {...methods}
                              label="Instructions"
                              name="scan_instructions"
                              // A textarea, not a single line: this holds a sentence or two about
                              // where the tag sits, and at one line the end of it scrolls out of
                              // sight while you are still writing it. Plain multiline rather than
                              // RichTextEditorField (§30's mapping for long text) because the app
                              // strips this to plain text before rendering it in the drawer — a
                              // formatting toolbar would offer marks the customer never sees.
                              multiline
                              minRows={3}
                              placeholder="The tag is sewn into the inner left seam."
                              helperText={fieldState.error?.message ?? "optional"}
                              value={field.value == null ? "" : String(field.value)}
                              onValueChange={(value) => field.onChange(value)}
                              onFieldBlur={field.onBlur}
                            />
                          )}
                        />

                        <Controller
                          name="scan_media_url"
                          control={methods.control}
                          render={({ field }) => (
                            <ScanMediaField
                              label="Photo or video"
                              value={field.value}
                              onChange={(url) => field.onChange(url)}
                            />
                          )}
                        />
                      </Box>
                    </Card>
                  </Box>
                </Collapse>

                {/* EXPERIENCE (VRG-782) — the brand's own narrative about this
                    product, shown as its own screen on the certificate.

                    Placed after Scan instructions and before Inventory: the form reads
                    from what the OBJECT is (media, basic, specs), through what a holder
                    needs in their hand (scan), into what the brand wants to SAY about
                    it, and only then into stock-keeping. Experience is the last
                    customer-facing card; Inventory is back-office.

                    RichTextEditorField per §30's column-type mapping (long/HTML text),
                    and unlike `scan_instructions` above — which is deliberately plain
                    multiline because the app strips it to plain text — the marks here
                    genuinely survive to the reader. */}
                <Collapse in={EXPERIENCE_ENABLED && isSectionVisible(sectionById("experience"))} timeout={250} unmountOnExit>
                  <Box id="experience" sx={sectionSx}>
                    <Card title="Experience">
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                        {/* Two sentences, not one em-dash-joined clause (§15). It has to
                            say WHERE this lands, because the effect is felt on a screen
                            the author is not looking at (§67) — and it has to say what
                            an empty field means, which is the half people get wrong. */}
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          Your own story about this product. It gets its own Experience
                          screen on every certificate for this SKU, separate from the
                          record itself. Leave it blank and no Experience screen appears.
                        </Typography>

                        <Controller
                          name="experience_content"
                          control={methods.control}
                          render={({ field }) => (
                            <RichTextEditorField
                              label="Story"
                              value={field.value ?? ""}
                              onChange={field.onChange}
                              placeholder="How it was made, what it is made of, how to care for it…"
                              helperText="Links and images are not shown on the certificate. Formatting and lists are."
                              minHeight="240px"
                            />
                          )}
                        />
                      </Box>
                    </Card>
                  </Box>
                </Collapse>

                {/* REVEAL (VRG-790) — plays full-screen after a tag verifies, before the
                    certificate renders. */}
                <Collapse in={isSectionVisible(sectionById("reveal"))} timeout={250} unmountOnExit>
                  <Box id="reveal" sx={sectionSx}>
                    <Card title="Reveal">
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                        {/* §67 — this setting takes effect on a surface the person editing it
                            cannot see from here, so the copy says WHERE and WHEN. Separate
                            sentences rather than em-dash-joined clauses, lineHeight 1.5 (§15). */}
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          A short video that plays after someone taps one of this product's tags
                          and it verifies, before the certificate appears. They can tap to skip
                          it, and it only plays the first time they view a certificate. Upload it
                          whenever you like and turn it on when the drop goes live.
                        </Typography>

                        <Controller
                          name="reveal_video_url"
                          control={methods.control}
                          render={({ field }) => (
                            <RevealVideoField
                              videoUrl={field.value}
                              posterUrl={methods.watch("reveal_poster_url")}
                              onChange={({ videoUrl, posterUrl }) => {
                                field.onChange(videoUrl);
                                /*
                                  `shouldDirty` so the form's Save gate (§54) sees the
                                  upload. Without it a brand could upload a reveal and
                                  find Save still disabled, because the uploads happen
                                  outside RHF's own change path.
                                */
                                methods.setValue("reveal_poster_url", posterUrl, {
                                  shouldDirty: true,
                                });
                                /*
                                  Clearing the video also clears the SWITCH. The server
                                  would resolve an enabled-but-empty reveal to nothing
                                  anyway (resolveReveal), so leaving the toggle on would
                                  show a brand an "on" reveal that cannot play — the one
                                  state the separate column makes possible and the form
                                  should never leave behind.
                                */
                                if (!videoUrl) {
                                  methods.setValue("reveal_enabled", false, {
                                    shouldDirty: true,
                                  });
                                }
                              }}
                            />
                          )}
                        />

                        <Box>
                          <FieldLabel label="Play this reveal" />
                          {/* Matches the Availability control's height so the toggle centers
                              against the fields above it. */}
                          <Stack direction="row" alignItems="center" spacing={3} sx={{ minHeight: "49px" }}>
                            <Controller
                              name="reveal_enabled"
                              control={methods.control}
                              defaultValue={false}
                              render={({ field }) => {
                                // Both halves are required for a reveal to play at all, so
                                // the switch is unreachable until they exist rather than
                                // flippable into a state the server resolves to nothing.
                                const ready = Boolean(
                                  methods.watch("reveal_video_url") &&
                                    methods.watch("reveal_poster_url"),
                                );
                                return (
                                  <>
                                    <Switch
                                      checked={Boolean(field.value) && ready}
                                      disabled={!ready}
                                      onChange={(_, checked) => field.onChange(checked)}
                                    />
                                    <Typography variant="body3" color="text.secondary">
                                      {ready
                                        ? Boolean(field.value)
                                          ? "Playing for everyone who taps a tag."
                                          : "Saved, but not playing yet."
                                        : "Add a video and a poster frame first."}
                                    </Typography>
                                  </>
                                );
                              }}
                            />
                          </Stack>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                </Collapse>

                {/* 3D MODEL (VRG-749) — replaces the hero still on the certificate
                    page and the product page when set. */}
                <Collapse in={isSectionVisible(sectionById("model"))} timeout={250} unmountOnExit>
                  <Box id="model" sx={sectionSx}>
                    <Card title="3D model">
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                        {/* §67 — this setting takes effect on a surface the person editing
                            it cannot see from here, so the copy says WHERE. lineHeight 1.5
                            and separate sentences rather than em-dash-joined clauses (§15). */}
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          Upload this product as a 3D model and it becomes the main image on
                          every certificate for it, in place of the first photo. Customers can
                          spin it, and on a phone they can place it in the room. Leave it empty
                          to keep using your photos.
                        </Typography>

                        <Controller
                          name="model_url"
                          control={methods.control}
                          render={({ field }) => (
                            <ProductModelField
                              label="Model"
                              value={field.value}
                              onChange={(url) => field.onChange(url)}
                            />
                          )}
                        />
                      </Box>
                    </Card>
                  </Box>
                </Collapse>

                {/* INVENTORY (availability + quantity merged) */}
                <Collapse in={isSectionVisible(sectionById("inventory"))} timeout={250} unmountOnExit>
                  <Box id="inventory" sx={sectionSx}>
                    <Card title="Inventory">
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "40px" }}>
                        <Box>
                          <FieldLabel label="Availability" />
                          {/* Match the Quantity control height (49px) so the toggle
                              centers vertically alongside the quantity selector. */}
                          <Stack direction="row" alignItems="center" spacing={3} sx={{ minHeight: "49px" }}>
                            <Controller
                              name="available"
                              control={methods.control}
                              defaultValue={available}
                              render={({ field }) => (
                                <Switch
                                  checked={field.value ?? available}
                                  onChange={(_, checked) => {
                                    field.onChange(checked);
                                    setAvailable(checked);
                                  }}
                                />
                              )}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {available ? "In stock" : "Out of stock"}
                            </Typography>
                          </Stack>
                        </Box>

                        <Box sx={{ maxWidth: "248px" }}>
                          <FieldLabel label="Quantity" />
                          <Controller
                            name="quantity"
                            control={methods.control}
                            rules={PRODUCT_RULES.quantity}
                            render={() => (
                              <Stack direction="row" gap="8px" alignItems="center">
                                <TextField
                                  // The whole `useForm()` return, like every other
                                  // TextField on this page. This site used to hand over
                                  // `formState` and `register` ALONE — which is exactly
                                  // the subset the component destructures, so it worked,
                                  // but `FieldProps` is declared as the whole
                                  // `UseFormReturn` and the partial surface never
                                  // satisfied it (TS2740, VRG-791). Spreading adds no
                                  // runtime behaviour: the members beyond those two are
                                  // never read.
                                  {...methods}
                                  name="quantity"
                                  value={quantity.toString()}
                                  type="number"
                                  inputProps={{ min: 0, max: 9999 }}
                                  rules={PRODUCT_RULES.quantity}
                                  sx={{
                                    flex: 1,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      "& fieldset": { borderColor: "grey.300" },
                                      "&:hover fieldset": { borderColor: "grey.400" },
                                      "&.Mui-focused fieldset": { borderColor: "primary.main" },
                                    },
                                    "& .MuiInputBase-input": {
                                      textAlign: "center",
                                      fontWeight: 500,
                                    },
                                  }}
                                  onValueChange={(value) => {
                                    const numValue = Number(value);
                                    setFormValue("quantity", numValue);
                                    setQuantity(numValue);
                                  }}
                                />
                                <IconButton
                                  onClick={handleDecrement}
                                  disabled={quantity <= 0}
                                  size="small"
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "8px",
                                    border: "1px solid",
                                    borderColor: "grey.300",
                                    backgroundColor: "white",
                                    "&:hover": { backgroundColor: "grey.50", borderColor: "grey.400" },
                                    "&:disabled": { backgroundColor: "grey.100", borderColor: "grey.200" },
                                  }}
                                >
                                  <RemoveIcon />
                                </IconButton>
                                <IconButton
                                  onClick={handleIncrement}
                                  size="small"
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "8px",
                                    border: "1px solid",
                                    borderColor: "grey.300",
                                    backgroundColor: "white",
                                    "&:hover": { backgroundColor: "grey.50", borderColor: "grey.400" },
                                  }}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Stack>
                            )}
                          />
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                </Collapse>

                {/* CONTACT CARD (VRG-720) — released to certificate viewers who have access. */}
                <Collapse in={isSectionVisible(sectionById("contact"))} timeout={250} unmountOnExit>
                  <Box id="contact" sx={sectionSx}>
                    <Card title="Contact card">
                      <ContactCardFields />
                    </Card>
                  </Box>
                </Collapse>

                {/* DEFAULT TAG (edit mode only) — set via the shared modal, not
                    the product Save (it's a separate relational endpoint). */}
                <Collapse in={page === "edit" && isSectionVisible(DEFAULT_TAG_SECTION)} timeout={250} unmountOnExit>
                  <Box id="defaulttag" sx={sectionSx}>
                    <Card title="Default tag">
                      {data?.tag_shop_product ? (
                        <Stack spacing={4} alignItems="flex-start">
                          <ProductPreview product={data.tag_shop_product as any} />
                          <Button
                            variant="secondary"
                            disableElevation
                            startIcon={<TagIcon strokeWidth={1} />}
                            onClick={() => setOpenDefaultTagModal(true)}
                          >
                            Change default tag
                          </Button>
                        </Stack>
                      ) : (
                        <Stack spacing={4} alignItems="flex-start">
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            Set a default tag for this product to enable direct ordering and advanced inventory alerts.
                          </Typography>
                          <Button
                            variant="secondary"
                            disableElevation
                            startIcon={<TagIcon strokeWidth={1} />}
                            onClick={() => setOpenDefaultTagModal(true)}
                          >
                            Set default tag
                          </Button>
                        </Stack>
                      )}
                    </Card>
                  </Box>
                </Collapse>

                {/* CLAIM ON SCAN (VRG-720) — edit mode and platform_admin only, HIDDEN for
                    everyone else (§27: nobody a brand could ask for it today). Staged into the
                    form and persisted by the ordinary Save, but turning it ON is gated BEFORE it
                    applies (§43): the switch does not move until the consequence is confirmed. */}
                <Collapse
                  in={page === "edit" && isPlatformAdmin && isSectionVisible(CLAIM_ON_SCAN_SECTION)}
                  timeout={250}
                  unmountOnExit
                >
                  <Box id="claim" sx={sectionSx}>
                    <Card title="Claim on scan">
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          The first person to tap one of these tags and confirm their email
                          becomes its owner. Built for Vera business cards.{" "}
                          <MuiLink component={RouterLink} to="/internal/docs/internal/internal-claim-on-scan">
                            Read the guide
                          </MuiLink>
                        </Typography>

                        <Box>
                          <FieldLabel
                            label="Claim on scan"
                            adornment={
                              <InfoTooltip
                                term="claim-on-scan"
                                text="Takes effect on the public certificate page of every certificate of this product. The access request becomes a Claim ownership button, and the first person who taps the tag and confirms their email becomes the owner. Turning it off stops new claims. Items already claimed stay with their owners."
                              />
                            }
                          />
                          <Stack direction="row" alignItems="center" spacing={3} sx={{ minHeight: "49px" }}>
                            <Controller
                              name="claim_on_scan"
                              control={methods.control}
                              defaultValue={false}
                              render={({ field }) => (
                                <Switch
                                  checked={Boolean(field.value)}
                                  onChange={(_, checked) => {
                                    // ON asks first and the switch stays where it is until the
                                    // answer is yes. OFF only ever reduces exposure, so it applies.
                                    if (checked) setConfirmClaimOnScan(true);
                                    else field.onChange(false);
                                  }}
                                />
                              )}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {methods.watch("claim_on_scan") ? "On" : "Off"}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                </Collapse>

                {/* VRG-101 / VRG-104 — curated custom fields. `CUSTOM_FIELDS_ENABLED`
                    (top of this file) is the one switch that hides the whole surface if
                    the certificate-side render has to be rolled back. Wrapped in the same
                    Collapse + `id` + `sectionSx` as every other section so the left TOC
                    scroll-spy and the search filter reach it. */}
                {CUSTOM_FIELDS_ENABLED && page === "edit" && id && data && (
                  <Collapse in={isSectionVisible(CUSTOM_FIELDS_SECTION)} timeout={250} unmountOnExit>
                    <Box id="customfields" sx={sectionSx}>
                      {/* VRG-796 — `Array.isArray`, not `?? []`. The column is untyped
                          jsonb and legacy rows hold other shapes; `??` guards only the
                          absent case, which is the one the type already describes, so
                          anything else reached the card's `[...initial]` spread and threw
                          `initial is not iterable` — taking the whole edit page to the
                          ErrorBoundary, which reads as the Edit button not working. The
                          API write paths are closed now (see `AddProductUseCase`), so this
                          is for rows written before that; it also matches what the three
                          other readers of this column already do. */}
                      <ProductCustomFieldsCard
                        productId={id}
                        initial={Array.isArray(data.custom_fields) ? data.custom_fields : []}
                      />
                    </Box>
                  </Collapse>
                )}

                {/* VRG-788 — which STANDARD fields appear on this SKU's certificates.
                    Sits directly below the custom-fields card: one card is the fields the
                    brand ADDS, the other the ones they can WITHHOLD, and the two are the
                    same question about the same page. Shares CUSTOM_FIELDS_ENABLED
                    deliberately — both halves of certificate curation appear together or
                    not at all, so a rollback cannot leave half a surface. */}
                {CUSTOM_FIELDS_ENABLED && page === "edit" && id && data && (
                  <Collapse in={isSectionVisible(CERTIFICATE_FIELDS_SECTION)} timeout={250} unmountOnExit>
                    <Box id="certificatefields" sx={sectionSx}>
                      <CertificateFieldsCard
                        productId={id}
                        initial={(data as any).hidden_certificate_fields}
                      />
                    </Box>
                  </Collapse>
                )}

                {/* No-match empty state */}
                {visibleSections.length === 0 && (
                  <Box sx={{ py: 10, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No fields match "{search}"
                    </Typography>
                  </Box>
                )}
              </FormProvider>
            </Box>

            <TocSpacer />
          </Box>

          {/* The fixed bottom action bar lived here — Cancel + Create/Save now sit in the
              sticky Header above (see the comment there). */}

          <ConfirmSettingModal
            open={confirmClaimOnScan}
            onClose={() => setConfirmClaimOnScan(false)}
            onConfirm={() => {
              // Stage it in the form; it is stored when the page is saved.
              setFormValue("claim_on_scan", true, { shouldDirty: true });
              setConfirmClaimOnScan(false);
            }}
            title="Turn on claim on scan?"
            consequence="Every certificate of this product that nobody has claimed yet will go to the first person who taps its tag and confirms their email. The old owner is not asked and is not emailed. Save the product for this to take effect."
            confirmLabel="Turn on claim on scan"
          />

          <SetDefaultTagModal
            product={data}
            open={openDefaultTagModal}
            onClose={() => setOpenDefaultTagModal(false)}
            refetch={refetch}
          />
        </>
      )}
    </>
  );
};
